import React, { useState, useRef, useEffect, useContext } from "react";
import {
  checkedIcon,
  keyboard_arrow_down,
  logoDefault,
} from "../assets/images/images";
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "../assets/images/CloseIcon";
import {
  publish,
  subscribe,
  unsubscribe,
} from "../services/loadingServices/loading.service";
import LanguageContext from "../context/LanguageContext";
import { updateCurrentActive } from "../services/api/locations.services";
import Loading from "./Loading";
import { showApiError } from "../services/apiError";
import { MenuItem, buildMenuFromBackend, menuSidebarTest } from "./sidebarMenu";
import SwitchCompanyPopover from "./SwitchCompanyPopover";
import { AccessContext, DEFAULT_ACCESS_CONTEXT } from "../services/accessContext";
import { switchCompany } from "../services/companySwitch";
import { selectedLocation } from "../services/currentLocation";
export { menuSidebarTest } from "./sidebarMenu";

declare global {
  interface Window {
    languageContext: any;
    modulesContext: any;
  }
}

interface SidebarCoreProps {
  userInfor: any;
  listCompany: any;
  getAccountInfor: (isLoading: boolean) => Promise<void>;
  accessContext?: AccessContext;
  /** Logo of the current company (not the home one); empty shows the default. */
  companyLogo?: string;
}


const Dropdown = ({
  children,
  isActive,
}: {
  children: React.ReactNode;
  isActive: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isActive && ref.current) {
      setHeight(ref.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isActive, children]);

  return (
    <div
      style={{ height }}
      className="overflow-hidden transition-all duration-300 ease-in-out"
    >
      <div ref={ref}>{children}</div>
    </div>
  );
};

const SidebarCore = ({
  userInfor,
  listCompany,
  getAccountInfor,
  accessContext = DEFAULT_ACCESS_CONTEXT,
  companyLogo,
}: SidebarCoreProps) => {
  const [selectedMenu, setSelectedMenu] = useState("Accounting");
  const path = window.location.pathname;
  const id: string = path.split('/').pop() || "";
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState<MenuItem[]>(menuSidebarTest);
  const nav = useNavigate();
  const location = useLocation();
  const preLanguage = localStorage.getItem("language");
  const showOptionRef = useRef<HTMLDivElement>(null);
  const switcherTriggerRef = useRef<HTMLButtonElement>(null);
  const userId: string = localStorage.getItem("userId") || "";
  const companyId: string = localStorage.getItem("companyId") || "";
  const currentPath = `/${location.pathname.split("/")[2]}`;
  const [showOption, setShowOption] = useState<boolean>(false);
  const [language, setLanguage] = useState<any>(LanguageContext);
  const specialPathMapping: Record<string, string> = {
    "/deviceUserRole": "Administration",
    "/roles": "Administration",
    "/count": "Inventory Management",
    "/countsApproval": "Inventory Management",
    "/orders": "Order Management",
    "/orderApproval": "Order Management",
    "/deliveries": "Order Management",
    "/groups": "Items"
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showOptionRef.current &&
        !showOptionRef.current.contains(event.target as Node)
      ) {
        setShowOption(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);


  const normalizePath = (path: string) => {
    // remove language nếu có
    const noLang = path.replace(/^\/[a-z]{2}\//, "/");
    // lấy segment đầu tiên
    return "/" + noLang.split("/").filter(Boolean)[0];
  };

  useEffect(() => {
    const handleRolePermission = (data: any) => {
      const backendMenuData = data.detail;
      let builtMenus = buildMenuFromBackend(backendMenuData);
      const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}\//, "/");
      const pathSegments = pathWithoutLang.split('/').filter(Boolean);
      const basePath = normalizePath(pathWithoutLang);
      const potentialId = pathSegments.length > 1 ? pathSegments[pathSegments.length - 1] : null;
      const isUUID = potentialId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialId);

      const isPathMatch = (child: any, currentPath: string): boolean => {
        const childPath = child.path;
        const childId = child.id;
        if (isUUID && childId && potentialId === childId) {
          return true;
        }
        if (childPath === currentPath) return true;
        if (currentPath.startsWith(childPath + "/")) return true;
        return false;
      };

      builtMenus = builtMenus.map((section) => {
        // Xử lý đặc biệt cho Dashboard (không có children)
        if (section.title === "Dashboard" && section.path) {
          return {
            ...section,
            isActive: pathWithoutLang === section.path || pathWithoutLang.startsWith(section.path + "/"),
          };
        }

        // Xử lý cho các menu có children
        const matchedChild = section.children?.find((child: any) =>
          isPathMatch(child, pathWithoutLang)
        );

        const childMatch = !!matchedChild;
        const basePath = normalizePath(pathWithoutLang);
        const isSpecialCase = specialPathMapping[basePath] === section.title;

        const updatedChildren = section.children?.map((child: any) => ({
          ...child,
          isActive: isPathMatch(child, pathWithoutLang),
        }));

        return {
          ...section,
          isActive: isSpecialCase || childMatch,
          children: updatedChildren || section.children,
        };
      });

      setMenus(builtMenus);
      setLoading(false);
      const handleSidebarClick = (childPath: any) => {

        const path = builtMenus
          .flatMap((v: any) => v.children)
          .filter((val: any) => val?.title)
          .find((v: any) => v.title === childPath.detail.value);
        setMenus((prevMenus) =>
          prevMenus.map((menu) => {
            if (menu.children) {
              const hasChildActive = menu.children.some(
                (child) => child.title === childPath.detail.value
              );
              const updatedChildren = menu.children.map((child) => ({
                ...child,
                isActive: child.title === childPath.detail.value,
              }));
              return {
                ...menu,
                isActive: hasChildActive,
                children: updatedChildren,
              };
            } else {
              return {
                ...menu,
                isActive: menu.title === childPath.detail.value,
              };
            }
          })
        );

        if (path?.path) {
          nav(`/${preLanguage}${path.path}`);
        }
      };

      const webChangeSidebarHandler = (data: any) => {
        const clickedTitle = data.detail.value;
        const parentMenu = builtMenus.find(
          (section) =>
            section.title === clickedTitle ||
            section.children?.some((child: any) => child.title === clickedTitle)
        );
        const targetPath =
          parentMenu?.children?.find((v: any) => v.title === clickedTitle)?.path ?? "";

        if (parentMenu && targetPath) {
          setSelectedMenu(parentMenu.title);
          nav(`/${preLanguage}${targetPath}${data.detail.createNew ? "/new" : ""}`);
        }
      };

      subscribe("webChangeSidebar", webChangeSidebarHandler);
      subscribe("changeActiveRedirect", handleSidebarClick);

      return () => {
        unsubscribe("webChangeSidebar", webChangeSidebarHandler);
        unsubscribe("changeActiveRedirect", handleSidebarClick);
      };
    };

    subscribe("modulePermission", handleRolePermission);
    return () => {
      unsubscribe("modulePermission", handleRolePermission);
    };
  }, [location.pathname, nav, preLanguage]);
  const handleUpdateLocation = async (id: string) => {
    publish("loading", true);
    // Support must not move the customer's company-wide branch, so the pick stays on this device.
    if (accessContext.mode === "support") {
      localStorage.setItem("locationId", id);
      window.location.reload();
      return;
    }
    try {
      await updateCurrentActive({
        id,
        companyId,
        userId,
      });
      localStorage.setItem("locationId", id);
      await getAccountInfor(true);
      window.location.reload();
    } catch (error) {
      // A failed switch does not reload, so the error toast stays on screen.
      showApiError(error, { language });
      publish("loading", false);
    }
  };

  const handleUpdateComopaniesLocation = async (id: string) => {
    publish("loading", true);
    try {
      // An editor with unsaved changes can hold the switch; its Discard runs this again.
      const left = await switchCompany(id, { resume: () => handleUpdateComopaniesLocation(id) });
      if (!left) publish("loading", false);
    } catch (error) {
      // A failed switch does not reload, so the error toast stays on screen.
      showApiError(error, { language });
      publish("loading", false);
    }
  };

  const toggleMenu = (sectionTitle: string, menuIndex: number) => {
    setMenus((prev) =>
      prev.map((menu, idx) => ({
        ...menu,
        isActive: idx === menuIndex ? !menu.isActive : false,
      }))
    );
  };

  const handleSelectMenuSidebar = (menu: any, idx: any) => {
    if (menu.children) {
      toggleMenu(menu?.title, idx);
    } else if (menu.path) {
      handleSidebarClick(menu.path);
      setMenus((prev) =>
        prev.map((m, idx) => ({
          ...m,
          isActive: m.path === menu.path ? !m.isActive : false,
        }))
      );
    }
  };

  const handleSidebarClick = (val: any) => {
    if (val?.title) {
      localStorage.setItem("title", val?.title ?? '')
    }
    if (val === "/dashboard") {
      nav(`/${preLanguage}/${val?.path}`);
    } else {
      nav(`/${preLanguage}${val?.path}`);
    }
  };

  const handleNaviate = () => {
    publish("webChangeSidebar", { value: "Locations", createNew: true });
  };

  const currentBranch = selectedLocation(userInfor?.locations, accessContext);

  return (
    <div className="w-64 bg-[#F8F8F8] font-inter text-sm text-[#222525] border-r border-r-[#EBF0F0] pt-9 px-3 relative overflow-hidden h-full">
      {loading ? (
        <div className="w-full h-full">
          <Loading />
        </div>
      ) : (
        <div className="h-[calc(100%-66px-30px)] overflow-y-scroll scroll-container">
          {menus.map((menu, idx) => (
            <div key={idx} className="mb-2">
              <button
                onClick={() => {
                  handleSelectMenuSidebar(menu, idx);
                }}
                className="flex items-center justify-between w-full px-3 py-2 rounded hover:bg-[#EBF0F0]"
              >
                <span className="flex items-center gap-2">
                  <div className="w-6-h-6 flex-items-center justify-center">
                    <menu.icon active={menu.isActive} size={24} />
                  </div>
                  {language[menu.title] ?? menu.title}
                </span>
                {menu.children && (
                  <img
                    src={keyboard_arrow_down}
                    alt="arrow"
                    className={`transform transition-transform duration-300 ${menu.isActive ? "rotate-180" : ""
                      }`}
                  />
                )}
              </button>

              {menu.children && (
                <Dropdown isActive={menu.isActive ?? false}>
                  <ul className="pl-8 pr-2 py-2 text-sm space-y-[2px]">
                    {menu.children
                      .filter((child: any) => child.title !== "Billing" && child.title !== "Users" && child.title !== "Invoice configurations")
                      .map((child: any, cIdx) => {
                        const basePath = normalizePath(currentPath);
                        // Full pathname (language prefix stripped) so multi-segment
                        // routes like "/reporting/sales-overview" match correctly.
                        const fullPath = location.pathname.replace(/^\/[a-z]{2}\//, "/");
                        const specialChildMapping: Record<string, string> = {
                          "/deviceUserRole": "Roles",
                          "/roles": "Roles",
                          "/count": "Stock Counts",
                          "/countsApproval": "Stock Counts",
                          "/orders": "Order Management",
                          "/orderApproval": "Order Management",
                          "/deliveries": "Order Management",
                          "/groups": "Items",
                        };

                        const childActive =
                          child.path === fullPath ||
                          fullPath.startsWith(child.path + "/") ||
                          specialChildMapping[basePath] === child.title;

                        return (
                          <li
                            onClick={() => {
                              if (child.title === "Journal Entries" || child.path === "/journal-entries") {
                                console.log("Clicked Journal Entries via proper navigate");
                                const lang = localStorage.getItem("language") || "en";
                                nav(`/${lang}/journal-entries`);
                              } else {
                                handleSidebarClick(child);
                              }
                            }}
                            key={cIdx}
                            className={`flex items-center gap-2 px-3 py-2 cursor-pointer font-medium rounded-lg text-sm font-inter
        ${childActive || id === child?.id
                                ? "bg-[#EBF0F0] text-[#0F7679]"
                                : "hover:bg-[#EBF0F0] hover:text-[#0F7679]"
                              }`}
                          >
                            {language[child.title] ?? child.title}
                          </li>
                        );
                      })}


                  </ul>
                </Dropdown>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="relative">
        {/* A real button, so the switcher (and "Find a customer") can be opened from the keyboard. */}
        <button
          type="button"
          ref={switcherTriggerRef}
          aria-expanded={showOption}
          onClick={() => {
            setShowOption(!showOption);
            }}
          className={`duration-300 w-full text-left h-[66px] rounded-xl bg-white flex items-center justify-center px-3 cursor-pointer border ${showOption ? "border-[#189296]" : "border-transparent"
            } `}
        >
          <span className="flex items-center gap-x-3 w-full">
            <span className="border border-[#DBE4E4] flex items-center justify-center h-[40px] w-[40px] rounded-full text-[#6A7878] uppercase overflow-hidden">
              <img
                src={companyLogo || logoDefault}
                alt=""
                className="object-contain"
              />
            </span>
            <span>
              <span className="block font-semibold text-base text-[#05292A]">
                {accessContext.currentCompanyName
                  || listCompany?.find((v: any) => v.isCurrentActive)?.company?.companyName
                  || language["Company"]
                  || "Company"}
              </span>
              <span className="block text-[12px] font-normal leading-4 text-[#6A7878]">
                {currentBranch?.name
                  || currentBranch?.location?.name
                  || language["Head Office"]
                  || "Head Office"}
              </span>
            </span>
          </span>
          <span className="w-4">
            <span className="flex items-center justify-center w-4 h-4">
              <img src={keyboard_arrow_down} alt="" className="rotate-[180deg]" />
            </span>
            <span className="flex items-center justify-center w-4 h-4">
              <img src={keyboard_arrow_down} alt="" />
            </span>
          </span>
        </button>
        <SwitchCompanyPopover
          open={showOption}
          onClose={() => {
            setShowOption(false);
            switcherTriggerRef.current?.focus();
          }}
          popoverRef={showOptionRef}
          language={language}
          listCompany={listCompany}
          userInfor={userInfor}
          accessContext={accessContext}
          onSelectCompany={handleUpdateComopaniesLocation}
          onSelectLocation={handleUpdateLocation}
          onAddLocation={handleNaviate}
        />
      </div>
    </div>
  );
};

export default SidebarCore;
